import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Switch, FormControlLabel, TextField, Button, Grid, Chip, Alert,
  CircularProgress, InputAdornment, Divider, Radio, RadioGroup, FormControl, FormLabel,
  Select, MenuItem, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar
} from '@mui/material';
import axios from 'axios';
import {
  API, getAuthHeader, money, fmtDate, fmtDay, numField, planName, intervalLabel,
  vehicleTypeLabel, CLASS_MULTIPLIERS, round2, isBlank
} from './customerApp/shared';
import PlansSection, { plansPayload } from './customerApp/PlansSection';
import ClaimsSection from './customerApp/ClaimsSection';
import { useOrg } from '../context/OrgContext';

const CLASS_TYPES = ['Light Duty', 'Medium Duty', 'Heavy Duty', 'Service Truck'];
// Mirrors SERVICE_ICONS in backend/models/CustomerAppConfig.js
const SERVICE_ICONS = [
  { key: 'tow', label: 'Tow truck' },
  { key: 'jump', label: 'Battery' },
  { key: 'lockout', label: 'Lock' },
  { key: 'fuel', label: 'Fuel pump' },
  { key: 'tire', label: 'Tire' },
  { key: 'winch', label: 'Winch' },
  { key: 'mechanic', label: 'Mechanic' },
  { key: 'generic', label: 'Wrench (generic)' }
];
const slugify = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 32);
const PRICE_CLASSES = ['medium', 'heavy'];
const PRICE_FIELDS = [
  { key: 'basePrice', label: 'Base', money: true, width: 96, step: 0.01 },
  { key: 'includedMiles', label: 'Miles', money: false, width: 70, step: 1 },
  { key: 'perMileRate', label: '/ mi', money: true, width: 90, step: 0.01 }
];

/** Blank-field placeholder: what the backend derives for a class when nothing explicit is set. */
const derivedClassValue = (light, cls, field) => {
  const base = light?.[field];
  if (isBlank(base)) return '';
  if (field === 'includedMiles') return String(base);
  return String(round2(Number(base) * (CLASS_MULTIPLIERS[cls] || 1)));
};

const classCellSx = { px: 0.5, py: 0.5, whiteSpace: 'nowrap' };

function DispatchSection({ config, setField }) {
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>Dispatch</Typography>
      <FormControl sx={{ mb: 2 }}>
        <FormLabel>Dispatch mode</FormLabel>
        <RadioGroup
          row
          value={config.dispatchMode || 'Auto'}
          onChange={(e) => setField('dispatchMode', e.target.value)}
        >
          <FormControlLabel value="Auto" control={<Radio />} label="Auto-dispatch (nearest available driver)" />
          <FormControlLabel value="Blast" control={<Radio />} label="Blast (offer to all drivers in radius)" />
        </RadioGroup>
      </FormControl>
      <FormControl sx={{ mb: 2, display: 'block' }}>
        <FormLabel>Phone verification</FormLabel>
        <RadioGroup
          row
          value={config.phoneVerificationMode || 'sms'}
          onChange={(e) => setField('phoneVerificationMode', e.target.value)}
        >
          <FormControlLabel value="sms" control={<Radio />} label="Text a 6-digit code (Telnyx)" />
          <FormControlLabel value="auto" control={<Radio />} label="Auto-verify every phone (until the texting number is approved)" />
        </RadioGroup>
        {config.phoneVerificationMode === 'auto' && (
          <Typography variant="caption" color="warning.main">
            Sign-ups and any code entered are accepted without a text. Switch back to texting once Telnyx approves the number.
          </Typography>
        )}
      </FormControl>
      <Grid container spacing={2}>
        {config.dispatchMode === 'Blast' && (
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              {...numField({ label: 'Blast initial radius' })}
              value={config.blastInitialRadiusMiles ?? ''}
              onChange={(e) => setField('blastInitialRadiusMiles', e.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end">mi</InputAdornment> }}
              inputProps={{ min: 1, max: 500 }}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth size="small" label="Support phone"
            value={config.supportPhone || ''}
            onChange={(e) => setField('supportPhone', e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            {...numField({ label: 'Tax' })}
            value={config.taxPercent ?? ''}
            onChange={(e) => setField('taxPercent', e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            inputProps={{ min: 0, max: 50, step: 0.01 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            {...numField({ label: 'GOA fee' })}
            value={config.goaFeePercent ?? ''}
            onChange={(e) => setField('goaFeePercent', e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            inputProps={{ min: 0, max: 100 }}
            helperText="Of the quoted price, charged when the customer is gone on arrival"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField
            {...numField({ label: 'Tow road factor' })}
            value={config.towRoadFactor ?? ''}
            onChange={(e) => setField('towRoadFactor', e.target.value)}
            inputProps={{ min: 1, max: 2, step: 0.05 }}
            helperText="Straight-line miles x factor = billed tow miles"
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

/** One compact numeric cell for the pricing tables. */
function PriceCell({ field, value, placeholder, onChange }) {
  return (
    <TableCell sx={classCellSx}>
      <TextField
        {...numField({ fullWidth: false })}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        InputProps={field.money ? { startAdornment: <InputAdornment position="start">$</InputAdornment> } : undefined}
        inputProps={{ min: 0, step: field.step, style: { paddingLeft: 4, paddingRight: 4 } }}
        sx={{ width: field.width }}
      />
    </TableCell>
  );
}

/** Light / Medium / Heavy two-row grouped header shared by the base table and the overrides. */
function ClassHeaderRows({ leading, trailing = [] }) {
  return (
    <>
      <TableRow>
        {leading.map((h) => <TableCell key={h} rowSpan={2} sx={{ verticalAlign: 'bottom' }}>{h}</TableCell>)}
        <TableCell colSpan={3} align="center" sx={{ borderLeft: 1, borderColor: 'divider' }}>Light</TableCell>
        <TableCell colSpan={3} align="center" sx={{ borderLeft: 1, borderColor: 'divider' }}>Medium</TableCell>
        <TableCell colSpan={3} align="center" sx={{ borderLeft: 1, borderColor: 'divider' }}>Heavy</TableCell>
        {trailing.map((h, i) => <TableCell key={`t${i}`} rowSpan={2}>{h}</TableCell>)}
      </TableRow>
      <TableRow>
        {['light', ...PRICE_CLASSES].map((cls) => PRICE_FIELDS.map((f, i) => (
          <TableCell
            key={`${cls}-${f.key}`}
            sx={{ ...classCellSx, ...(i === 0 ? { borderLeft: 1, borderColor: 'divider' } : {}) }}
          >
            <Typography variant="caption" color="text.secondary">{f.label}</Typography>
          </TableCell>
        )))}
      </TableRow>
    </>
  );
}

const EMPTY_SERVICE = {
  key: '', label: '', jobService: '', classType: 'Service Truck', icon: 'generic', description: '',
  basePrice: '', includedMiles: 0, perMileRate: 0, classPricing: {}, needsDropoff: false, cashAllowed: true, enabled: true
};

/** Add or edit one catalog service; `existingKeys` guards against a duplicate key. */
function ServiceDialog({ open, initial, existingKeys, onClose, onSave }) {
  const [form, setForm] = useState(initial || EMPTY_SERVICE);
  const [keyTouched, setKeyTouched] = useState(!!initial?.key);
  const [error, setError] = useState('');
  const isNew = !initial?.key;

  useEffect(() => {
    if (!open) return;
    setForm(initial || EMPTY_SERVICE);
    setKeyTouched(!!initial?.key);
    setError('');
  }, [open, initial]);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const setLabel = (value) => setForm((f) => ({ ...f, label: value, ...(keyTouched ? {} : { key: slugify(value) }) }));
  const setClass = (cls, field, value) => setForm((f) => ({
    ...f, classPricing: { ...(f.classPricing || {}), [cls]: { ...(f.classPricing?.[cls] || {}), [field]: value } }
  }));

  const save = () => {
    const key = slugify(form.key);
    if (!form.label.trim()) return setError('A label is required');
    if (!/^[a-z0-9_]{2,32}$/.test(key)) return setError('Key must be 2–32 lowercase letters, digits or underscores');
    if (isNew && existingKeys.includes(key)) return setError(`A service with key "${key}" already exists`);
    if (!String(form.jobService || '').trim()) return setError('A job service name is required (what dispatch sees on the job)');
    if (isBlank(form.basePrice) || Number(form.basePrice) < 0) return setError('A base price is required');
    onSave({ ...form, key, label: form.label.trim(), jobService: String(form.jobService).trim() });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isNew ? 'Add service' : `Edit ${initial.label}`}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={7}>
            <TextField fullWidth size="small" label="Label" value={form.label} onChange={(e) => setLabel(e.target.value)} helperText="What customers see" />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth size="small" label="Key" value={form.key} disabled={!isNew}
              onChange={(e) => { setKeyTouched(true); set('key', e.target.value); }}
              helperText={isNew ? 'Internal id; letters, digits, _' : 'Cannot change'}
            />
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth size="small" label="Job service" value={form.jobService} onChange={(e) => set('jobService', e.target.value)}
              helperText="Service name on the dispatch job (e.g. Tow, Battery Jump, Lock Out)"
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Truck class</InputLabel>
              <Select label="Truck class" value={form.classType || 'Light Duty'} onChange={(e) => set('classType', e.target.value)}>
                {CLASS_TYPES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5}>
            <FormControl fullWidth size="small">
              <InputLabel>Icon</InputLabel>
              <Select label="Icon" value={form.icon || 'generic'} onChange={(e) => set('icon', e.target.value)}>
                {SERVICE_ICONS.map((i) => <MenuItem key={i.key} value={i.key}>{i.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={7}>
            <TextField
              fullWidth size="small" label="Description" value={form.description || ''} multiline minRows={2}
              onChange={(e) => set('description', e.target.value.slice(0, 300))} helperText={`${(form.description || '').length}/300 — shown on the website and in the app`}
            />
          </Grid>
          <Grid item xs={12}><Typography variant="subtitle2">Light-duty price</Typography></Grid>
          {PRICE_FIELDS.map((f) => (
            <Grid item xs={4} key={f.key}>
              <TextField
                {...numField({ label: f.label === '/ mi' ? 'Per mile' : f.label === 'Miles' ? 'Included miles' : 'Base price' })}
                value={form[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)}
                InputProps={f.money ? { startAdornment: <InputAdornment position="start">$</InputAdornment> } : undefined}
                inputProps={{ min: 0, step: f.step }}
              />
            </Grid>
          ))}
          {PRICE_CLASSES.map((cls) => (
            <React.Fragment key={cls}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>{cls}-duty price</Typography>
                <Typography variant="caption" color="text.secondary">Leave blank to derive from the light price ({CLASS_MULTIPLIERS[cls]}x)</Typography>
              </Grid>
              {PRICE_FIELDS.map((f) => (
                <Grid item xs={4} key={`${cls}-${f.key}`}>
                  <TextField
                    {...numField({ label: f.label === '/ mi' ? 'Per mile' : f.label === 'Miles' ? 'Included miles' : 'Base price' })}
                    value={form.classPricing?.[cls]?.[f.key] ?? ''} placeholder={derivedClassValue(form, cls, f.key)}
                    onChange={(e) => setClass(cls, f.key, e.target.value)}
                    InputProps={f.money ? { startAdornment: <InputAdornment position="start">$</InputAdornment> } : undefined}
                    inputProps={{ min: 0, step: f.step }}
                  />
                </Grid>
              ))}
            </React.Fragment>
          ))}
          <Grid item xs={12}>
            <FormControlLabel control={<Switch checked={!!form.needsDropoff} onChange={(e) => set('needsDropoff', e.target.checked)} />} label="Needs a drop-off location (tow)" />
            <FormControlLabel control={<Switch checked={form.cashAllowed !== false} onChange={(e) => set('cashAllowed', e.target.checked)} />} label="Accepts cash" />
            <FormControlLabel control={<Switch checked={form.enabled !== false} onChange={(e) => set('enabled', e.target.checked)} />} label="Offered" />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save}>{isNew ? 'Add service' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function PricingSection({ config, setField, regions }) {
  const services = config.services || [];
  const overrides = config.regionOverrides || [];
  const [regionToAdd, setRegionToAdd] = useState('');
  const [dialog, setDialog] = useState(null); // { index } for edit, {} for add
  const [confirmDelete, setConfirmDelete] = useState(null);

  const setService = (idx, key, value) => {
    const next = services.map((s, i) => (i === idx ? { ...s, [key]: value } : s));
    setField('services', next);
  };

  const moveService = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= services.length) return;
    const next = services.slice();
    [next[idx], next[to]] = [next[to], next[idx]];
    setField('services', next.map((s, i) => ({ ...s, sortOrder: i + 1 })));
  };

  const saveDialog = (service) => {
    if (dialog?.index != null) {
      setField('services', services.map((s, i) => (i === dialog.index ? { ...s, ...service } : s)));
    } else {
      setField('services', [...services, { ...service, sortOrder: services.length + 1 }]);
    }
    setDialog(null);
  };

  const deleteService = (idx) => {
    setField('services', services.filter((_, i) => i !== idx).map((s, i) => ({ ...s, sortOrder: i + 1 })));
    setField('regionOverrides', overrides.map((o) => ({ ...o, services: (o.services || []).filter((s) => s.key !== services[idx].key) })));
    setConfirmDelete(null);
  };

  const setServiceClass = (idx, cls, key, value) => {
    const next = services.map((s, i) => {
      if (i !== idx) return s;
      const classPricing = { ...(s.classPricing || {}), [cls]: { ...(s.classPricing?.[cls] || {}), [key]: value } };
      return { ...s, classPricing };
    });
    setField('services', next);
  };

  const regionName = (id) => regions.find((r) => String(r._id) === String(id))?.name || String(id);
  const availableRegions = regions.filter((r) => !overrides.some((o) => String(o.regionId) === String(r._id)));

  const addOverride = () => {
    if (!regionToAdd) return;
    setField('regionOverrides', [...overrides, { regionId: regionToAdd, services: [] }]);
    setRegionToAdd('');
  };

  const removeOverride = (regionId) => {
    setField('regionOverrides', overrides.filter((o) => String(o.regionId) !== String(regionId)));
  };

  const updateOverrideService = (regionId, serviceKey, updater) => {
    const next = overrides.map((o) => {
      if (String(o.regionId) !== String(regionId)) return o;
      const existing = (o.services || []).find((s) => s.key === serviceKey) || { key: serviceKey };
      const others = (o.services || []).filter((s) => s.key !== serviceKey);
      return { ...o, services: [...others, updater(existing)] };
    });
    setField('regionOverrides', next);
  };

  const setOverrideValue = (regionId, serviceKey, field, value) => (
    updateOverrideService(regionId, serviceKey, (s) => ({ ...s, [field]: value }))
  );

  const setOverrideClassValue = (regionId, serviceKey, cls, field, value) => (
    updateOverrideService(regionId, serviceKey, (s) => ({
      ...s,
      classPricing: { ...(s.classPricing || {}), [cls]: { ...(s.classPricing?.[cls] || {}), [field]: value } }
    }))
  );

  const overrideService = (o, serviceKey) => (o.services || []).find((s) => s.key === serviceKey) || {};
  const blankToEmpty = (v) => (isBlank(v) ? '' : v);

  /** Placeholder for a region's class cell: the explicit base-table value, else derived from the region's effective light price. */
  const overrideClassPlaceholder = (o, s, cls, field) => {
    const ov = overrideService(o, s.key);
    const explicit = s.classPricing?.[cls]?.[field];
    if (!isBlank(explicit)) return String(explicit);
    const light = {
      basePrice: isBlank(ov.basePrice) ? s.basePrice : ov.basePrice,
      includedMiles: isBlank(ov.includedMiles) ? s.includedMiles : ov.includedMiles,
      perMileRate: isBlank(ov.perMileRate) ? s.perMileRate : ov.perMileRate
    };
    return derivedClassValue(light, cls, field);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>Services and pricing</Typography>
          <Typography variant="body2" color="text.secondary">
            The services customers can order on the website and in the app, in this order. Customers are quoted base price plus per-mile over
            the included miles (tow only); tax is added on top. Light is the price for cars, SUVs, pickups, vans and motorcycles; leave Medium
            (towables) or Heavy (motorhomes, fifth wheels) blank to derive them at {CLASS_MULTIPLIERS.medium}x / {CLASS_MULTIPLIERS.heavy}x.
            Switch off <b>Cash</b> to make a service card-only. Changes apply when you save.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => setDialog({})} sx={{ flexShrink: 0 }}>Add service</Button>
      </Box>
      <TableContainer>
        <Table size="small" sx={{ '& .MuiTableCell-root': { px: 1 } }}>
          <TableHead>
            <ClassHeaderRows leading={['Order', 'On', 'Cash', 'Label', 'Job service', 'Class type', 'Drop-off']} trailing={['']} />
          </TableHead>
          <TableBody>
            {services.length === 0 && (
              <TableRow><TableCell colSpan={17}><Typography variant="body2" color="text.secondary">No services yet — add one.</Typography></TableCell></TableRow>
            )}
            {services.map((s, idx) => (
              <TableRow key={s.key}>
                <TableCell sx={{ px: 0.5, whiteSpace: 'nowrap' }}>
                  <Button size="small" sx={{ minWidth: 28, px: 0 }} disabled={idx === 0} onClick={() => moveService(idx, -1)} aria-label="Move up">▲</Button>
                  <Button size="small" sx={{ minWidth: 28, px: 0 }} disabled={idx === services.length - 1} onClick={() => moveService(idx, 1)} aria-label="Move down">▼</Button>
                </TableCell>
                <TableCell sx={{ px: 0.5 }}>
                  <Switch size="small" checked={s.enabled !== false} onChange={(e) => setService(idx, 'enabled', e.target.checked)} />
                </TableCell>
                <TableCell sx={{ px: 0.5 }}>
                  <Switch size="small" checked={s.cashAllowed !== false} onChange={(e) => setService(idx, 'cashAllowed', e.target.checked)} />
                </TableCell>
                <TableCell>
                  <TextField size="small" value={s.label || ''} onChange={(e) => setService(idx, 'label', e.target.value)} sx={{ minWidth: 120 }} />
                </TableCell>
                <TableCell><Typography variant="body2">{s.jobService}</Typography></TableCell>
                <TableCell>
                  <Select size="small" value={s.classType || 'Light Duty'} onChange={(e) => setService(idx, 'classType', e.target.value)} sx={{ minWidth: 130 }}>
                    {CLASS_TYPES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </TableCell>
                <TableCell sx={{ px: 0.5 }}>
                  <Switch size="small" checked={!!s.needsDropoff} onChange={(e) => setService(idx, 'needsDropoff', e.target.checked)} />
                </TableCell>
                {PRICE_FIELDS.map((f) => (
                  <PriceCell
                    key={`light-${f.key}`} field={f} value={s[f.key]}
                    onChange={(v) => setService(idx, f.key, v)}
                  />
                ))}
                {PRICE_CLASSES.map((cls) => PRICE_FIELDS.map((f) => (
                  <PriceCell
                    key={`${cls}-${f.key}`} field={f}
                    value={blankToEmpty(s.classPricing?.[cls]?.[f.key])}
                    placeholder={derivedClassValue(s, cls, f.key)}
                    onChange={(v) => setServiceClass(idx, cls, f.key, v)}
                  />
                )))}
                <TableCell sx={{ px: 0.5, whiteSpace: 'nowrap' }}>
                  <Button size="small" onClick={() => setDialog({ index: idx })}>Edit</Button>
                  <Button size="small" color="error" onClick={() => setConfirmDelete(idx)}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ServiceDialog
        open={!!dialog}
        initial={dialog?.index != null ? services[dialog.index] : null}
        existingKeys={services.map((s) => s.key)}
        onClose={() => setDialog(null)}
        onSave={saveDialog}
      />
      <Dialog open={confirmDelete != null} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Delete {confirmDelete != null ? services[confirmDelete]?.label : ''}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            It disappears from the website and app when you save. Jobs already created keep their service name.
            If you only want to stop offering it for now, switch it off instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Keep</Button>
          <Button color="error" variant="contained" onClick={() => deleteService(confirmDelete)}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" gutterBottom>Regional overrides</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Leave a field blank to use the default price above (greyed placeholder).
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="add-region-label">Add region</InputLabel>
          <Select
            labelId="add-region-label" label="Add region" value={regionToAdd}
            onChange={(e) => setRegionToAdd(e.target.value)}
          >
            {availableRegions.length === 0 && <MenuItem value="" disabled>No regions available</MenuItem>}
            {availableRegions.map((r) => <MenuItem key={r._id} value={r._id}>{r.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={addOverride} disabled={!regionToAdd}>Add override</Button>
      </Box>

      {overrides.map((o) => (
        <Paper key={String(o.regionId)} variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2">{regionName(o.regionId)}</Typography>
            <Button size="small" color="error" onClick={() => removeOverride(o.regionId)}>Remove</Button>
          </Box>
          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableCell-root': { px: 1 } }}>
              <TableHead>
                <ClassHeaderRows leading={['Service']} />
              </TableHead>
              <TableBody>
                {services.map((s) => {
                  const ov = overrideService(o, s.key);
                  return (
                    <TableRow key={s.key}>
                      <TableCell>{s.label}</TableCell>
                      {PRICE_FIELDS.map((f) => (
                        <PriceCell
                          key={`light-${f.key}`} field={f}
                          value={blankToEmpty(ov[f.key])}
                          placeholder={String(s[f.key] ?? '')}
                          onChange={(v) => setOverrideValue(o.regionId, s.key, f.key, v)}
                        />
                      ))}
                      {PRICE_CLASSES.map((cls) => PRICE_FIELDS.map((f) => (
                        <PriceCell
                          key={`${cls}-${f.key}`} field={f}
                          value={blankToEmpty(ov.classPricing?.[cls]?.[f.key])}
                          placeholder={overrideClassPlaceholder(o, s, cls, f.key)}
                          onChange={(v) => setOverrideClassValue(o.regionId, s.key, cls, f.key, v)}
                        />
                      )))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}
    </Paper>
  );
}

function StripeStatusSection({ status }) {
  const chip = (label, ok) => (
    <Chip
      label={`${label}: ${ok ? 'Configured' : 'Missing'}`}
      color={ok ? 'success' : 'error'}
      variant={ok ? 'filled' : 'outlined'}
      size="small"
    />
  );
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>Stripe status</Typography>
      {!status ? (
        <CircularProgress size={20} />
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {chip('Secret key', status.secretKey)}
            {chip('Publishable key', status.publishableKey)}
            {chip('Webhook secret', status.webhookSecret)}
            <Chip
              label={status.plansSynced ? 'Plans synced to Stripe' : 'Plans not synced'}
              color={status.plansSynced ? 'success' : 'warning'}
              variant="outlined" size="small"
            />
            <Chip
              label={status.introSynced ? 'Intro coupons synced' : 'Intro coupons not synced'}
              color={status.introSynced ? 'success' : 'warning'}
              variant="outlined" size="small"
            />
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Last webhook received: {fmtDate(status.lastWebhookAt)}
          </Typography>
        </>
      )}
      <Typography variant="caption" color="text.secondary">
        Stripe keys live in the server .env (STRIPE_SECRET_KEY, STRIPE_PUBLISHABLE_KEY, STRIPE_WEBHOOK_SECRET). Restart the API after changing them.
      </Typography>
    </Paper>
  );
}

const vehicleLine = (config, v) => {
  const desc = [v.year, v.make, v.model].filter(Boolean).join(' ');
  const plate = v.license || v.plate;
  return `${vehicleTypeLabel(config, v.type)}: ${desc || '—'}${plate ? ` (${plate})` : ''}`;
};

function CustomerDialog({ customerId, onClose, notify, config }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(null);
  const [sent, setSent] = useState({});

  useEffect(() => {
    if (!customerId) return;
    setData(null);
    setError('');
    setSent({});
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/api/customer-admin/customers/${customerId}`, { headers: getAuthHeader() });
        setData(d);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load customer');
      }
    })();
  }, [customerId]);

  const resend = async (jobId) => {
    setSending(jobId);
    try {
      const { data: r } = await axios.post(
        `${API}/api/customer-admin/customers/${customerId}/jobs/${jobId}/reimbursement`, {}, { headers: getAuthHeader() }
      );
      setSent((prev) => ({ ...prev, [jobId]: r }));
      notify(`Insurance packet sent to ${(r.sentTo || []).join(', ') || 'customer'}`, 'success');
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to send packet', 'error');
    } finally {
      setSending(null);
    }
  };

  const verifyPhone = async () => {
    setSending('verify');
    try {
      await axios.post(`${API}/api/customer-admin/customers/${customerId}/verify-phone`, {}, { headers: getAuthHeader() });
      setData((prev) => (prev ? { ...prev, customer: { ...prev.customer, phoneVerified: true } } : prev));
      notify('Phone marked as verified', 'success');
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to verify phone', 'error');
    } finally {
      setSending(null);
    }
  };

  const c = data?.customer;
  const sub = c?.subscription;
  const allowance = data?.allowance;
  const primary = data?.primary;
  const household = Array.isArray(c?.household) ? c.household : [];

  return (
    <Dialog open={!!customerId} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{c?.name || 'Customer'}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!data && !error && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}
        {c && (
          <>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}><Typography variant="body2"><b>Email:</b> {c.email || '—'}</Typography></Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" component="div">
                  <b>Phone:</b> {c.phone || '—'}{' '}
                  {c.phoneVerified
                    ? <Chip label="Verified" size="small" color="success" sx={{ ml: 1 }} />
                    : <Button size="small" variant="outlined" sx={{ ml: 1 }} onClick={verifyPhone} disabled={sending === 'verify'}>
                        {sending === 'verify' ? 'Verifying...' : 'Mark phone verified'}
                      </Button>}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" component="div">
                  <b>Plan:</b>{' '}
                  {primary ? (
                    <>Member of {primary.name || 'a primary'}&apos;s household{primary.email ? ` (${primary.email})` : ''}</>
                  ) : sub?.planKey ? (
                    <>
                      {planName(config, sub.planKey)} · {intervalLabel(sub.interval)} · {sub.status || 'unknown'}
                      {sub.currentPeriodEnd ? ` · renews ${fmtDay(sub.currentPeriodEnd)}` : ''}
                      {sub.cancelAtPeriodEnd ? ' · cancels at period end' : ''}
                      {sub.introApplied ? ' · intro applied' : ''}
                    </>
                  ) : 'None'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}><Typography variant="body2"><b>Joined:</b> {fmtDay(c.createdAt)}</Typography></Grid>
              <Grid item xs={12}>
                <Typography variant="body2">
                  <b>Knights:</b> {Number(c.knights?.balance || 0).toLocaleString()} balance
                  {c.knights?.earned ? ` (${Number(c.knights.earned).toLocaleString()} earned, ${Number(c.knights.redeemed || 0).toLocaleString()} redeemed)` : ''}
                  {c.referralCode ? ` · share code ${c.referralCode}` : ''}
                  {c.referredBy?.code ? ` · joined with ${c.referredBy.code}` : ''}
                  {c.referral?.code ? ` · affiliate ${c.referral.code}` : ''}
                </Typography>
              </Grid>
              {allowance && (
                <Grid item xs={12}>
                  <Typography variant="body2" component="div">
                    <b>Plan-year usage:</b>{' '}
                    {allowance.callsUsed ?? 0} of {allowance.includedCallsPerYear == null ? 'Unlimited' : allowance.includedCallsPerYear} calls used
                    {allowance.planYearEnd ? ` · resets ${fmtDay(allowance.planYearEnd)}` : ''}
                    {allowance.softCapExceeded && <Chip label="Soft cap exceeded" size="small" color="warning" sx={{ ml: 1 }} />}
                  </Typography>
                </Grid>
              )}
              {Array.isArray(c.vehicles) && c.vehicles.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2">
                    <b>Vehicles:</b> {c.vehicles.map((v) => vehicleLine(config, v)).join('; ')}
                  </Typography>
                </Grid>
              )}
            </Grid>

            {household.length > 0 && (
              <>
                <Typography variant="subtitle1" gutterBottom>Household members</Typography>
                <TableContainer sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Invited</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {household.map((m) => (
                        <TableRow key={m.customerId || m.email}>
                          <TableCell>{m.name || '—'}</TableCell>
                          <TableCell>{m.email || '—'}</TableCell>
                          <TableCell>
                            {m.status === 'active' || m.acceptedAt
                              ? <Chip label="Active" size="small" color="success" />
                              : <Chip label="Invited" size="small" />}
                          </TableCell>
                          <TableCell>{fmtDay(m.invitedAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            <Typography variant="subtitle1" gutterBottom>Recent requests</Typography>
            <TableContainer sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>PO</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Charge</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.jobs || []).length === 0 && (
                    <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">No requests yet</Typography></TableCell></TableRow>
                  )}
                  {(data.jobs || []).map((j) => {
                    const ch = j.customerCharge || {};
                    const packet = sent[j._id] || ch.reimbursement;
                    return (
                      <TableRow key={j._id}>
                        <TableCell>{j.po || '—'}</TableCell>
                        <TableCell>{j.status}</TableCell>
                        <TableCell>{j.service}</TableCell>
                        <TableCell>{fmtDate(j.createdAt)}</TableCell>
                        <TableCell>
                          {ch.coveredBySubscription ? 'Covered by plan' : money(ch.capturedAmountCents ?? ch.amountCents)}
                          {ch.status ? ` (${ch.status})` : ''}
                        </TableCell>
                        <TableCell align="right">
                          {j.status === 'Completed' && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                              <Button size="small" variant="outlined" disabled={sending === j._id} onClick={() => resend(j._id)}>
                                {sending === j._id ? 'Sending...' : 'Resend insurance packet'}
                              </Button>
                              {packet?.sentAt && (
                                <Typography variant="caption" color="text.secondary">
                                  Sent {fmtDate(packet.sentAt)}{packet.sentTo?.length ? ` to ${packet.sentTo.join(', ')}` : ''}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle1" gutterBottom>Payments</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Authorized</TableCell>
                    <TableCell>Captured</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Covered</TableCell>
                    <TableCell>Payment intent</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.payments || []).length === 0 && (
                    <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">No payments yet</Typography></TableCell></TableRow>
                  )}
                  {(data.payments || []).map((p) => (
                    <TableRow key={p._id || p.stripePaymentIntentId}>
                      <TableCell>{fmtDate(p.createdAt)}</TableCell>
                      <TableCell>{money(p.amountCents)}</TableCell>
                      <TableCell>{money(p.capturedAmountCents)}</TableCell>
                      <TableCell>{p.status || '—'}</TableCell>
                      <TableCell>{p.coveredBySubscription ? 'Yes' : 'No'}</TableCell>
                      <TableCell><Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{p.stripePaymentIntentId || '—'}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function CustomersSection({ notify, config }) {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ search: query, page: page + 1, limit }).toString();
        const { data } = await axios.get(`${API}/api/customer-admin/customers?${params}`, { headers: getAuthHeader() });
        if (cancelled) return;
        setRows(data.customers || []);
        setTotal(data.total || 0);
      } catch (err) {
        if (!cancelled) notify(err.response?.data?.message || 'Failed to load customers', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [query, page, limit, notify]);

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>Customers</Typography>
      <TextField
        size="small" placeholder="Search name, email or phone" value={search}
        onChange={(e) => setSearch(e.target.value)} sx={{ mb: 2, minWidth: 300 }}
      />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Verified</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Billing</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Joined</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={20} /></TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">No customers found</Typography></TableCell></TableRow>
            )}
            {rows.map((c) => (
              <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelected(c.id)}>
                <TableCell>{c.name || '—'}</TableCell>
                <TableCell>{c.email || '—'}</TableCell>
                <TableCell>{c.phone || '—'}</TableCell>
                <TableCell>{c.phoneVerified ? <Chip label="Yes" size="small" color="success" /> : <Chip label="No" size="small" />}</TableCell>
                <TableCell>{planName(config, c.subscription?.planKey) || (c.primaryCustomerId ? 'Household member' : '—')}</TableCell>
                <TableCell>{c.subscription?.planKey ? intervalLabel(c.subscription?.interval) : '—'}</TableCell>
                <TableCell>{c.subscription?.status || '—'}</TableCell>
                <TableCell>{fmtDay(c.createdAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div" count={total} page={page} rowsPerPage={limit}
        onPageChange={(e, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
      <CustomerDialog customerId={selected} onClose={() => setSelected(null)} notify={notify} config={config} />
    </Paper>
  );
}

/** Only the class columns the owner actually filled in; blank cells stay derived on the server. */
const classPricingPayload = (cp) => {
  if (!cp || typeof cp !== 'object') return undefined;
  const out = {};
  for (const cls of PRICE_CLASSES) {
    const col = cp[cls] || {};
    const cleaned = {};
    for (const f of PRICE_FIELDS) {
      if (!isBlank(col[f.key])) cleaned[f.key] = Number(col[f.key]);
    }
    if (Object.keys(cleaned).length) out[cls] = cleaned;
  }
  return Object.keys(out).length ? out : undefined;
};

/**
 * Customer app / website settings. `view` picks the Owner Console section:
 * 'services' (dispatch + services and pricing), 'memberships' (plans, intro
 * offer, Stripe status) or 'customers' (customer lookup and claims). Saving is
 * one payload whichever view is open, so nothing is lost between sections.
 */
/**
 * App-only price: the website shows the list price; a request placed in the app is
 * this much cheaper, and the home page announces it while it is on.
 */
function AppDiscountSection({ config, setField }) {
  const { customerName } = useOrg();
  const d = config.appDiscount || {};
  const set = (patch) => setField('appDiscount', { enabled: false, percentOff: 10, includeWebApp: false, headline: '', blurb: '', ...d, ...patch });
  const pct = Number(d.percentOff ?? 10);
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">App-only price</Typography>
        <FormControlLabel control={<Switch checked={!!d.enabled} onChange={(e) => set({ enabled: e.target.checked })} />} label={d.enabled ? 'On' : 'Off'} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Customers on wkroadss.com see the full price with a note of what the app would charge. A request placed in the {customerName} app gets the discount on the quote. While it is on, the website home page announces it and the app shows it on Home.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <TextField fullWidth size="small" type="number" label="Percent off" value={d.percentOff ?? 10} onChange={(e) => set({ percentOff: e.target.value })} inputProps={{ min: 0, max: 100, step: 1 }} helperText="off the price after any member discount" />
        </Grid>
        <Grid item xs={12} sm={9}>
          <FormControlLabel control={<Switch checked={!!d.includeWebApp} onChange={(e) => set({ includeWebApp: e.target.checked })} />} label="Also count app.wkroadss.com (the browser version of the app) as the app" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Headline" value={d.headline || ''} onChange={(e) => set({ headline: e.target.value })} placeholder={`Save {percent}% when you order in the app`} helperText="{percent} is replaced with the number above" inputProps={{ maxLength: 120 }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Blurb" value={d.blurb || ''} onChange={(e) => set({ blurb: e.target.value })} placeholder={`Get the ${customerName} app and every call is {percent}% off the website price.`} inputProps={{ maxLength: 300 }} />
        </Grid>
        {d.enabled && pct > 0 && (
          <Grid item xs={12}>
            <Alert severity="info">Example: a $100 call is $100 on the website and ${(100 - pct).toFixed(2)} in the app. Save settings to apply.</Alert>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}

const VIEW_TITLE = {
  services: ['Services & pricing', 'What you offer, what it costs, and how requests are dispatched.'],
  memberships: ['Memberships', 'The membership switch, the plans and the introductory offer.'],
  customers: ['Customers & claims', 'Look up a customer and decide reimbursement claims.']
};

function CustomerApp({ view = 'services' }) {
  const { demoMode, isPlatform } = useOrg(); // a partner's payments live under Company setup → Payments
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [regions, setRegions] = useState([]);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [snack, setSnack] = useState(null);

  const notify = useCallback((message, severity = 'info') => setSnack({ message, severity }), []);

  const loadStripeStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/customer-admin/stripe-status`, { headers: getAuthHeader() });
      setStripeStatus(data);
    } catch {
      setStripeStatus({ secretKey: false, publishableKey: false, webhookSecret: false, lastWebhookAt: null, plansSynced: false, introSynced: false });
    }
  }, []);

  const [loadError, setLoadError] = useState('');
  const load = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError('');
      const [{ data }, regionsRes] = await Promise.all([
        axios.get(`${API}/api/customer-admin/config`, { headers: getAuthHeader() }),
        axios.get(`${API}/api/regions`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      if (!data || !data.config) throw new Error('The server returned no settings');
      setConfig(data.config);
      setRegions(Array.isArray(regionsRes.data) ? regionsRes.data : []);
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to load customer app settings';
      setLoadError(`${message}${err.response?.status ? ` (${err.response.status})` : ''}`);
      notify(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); loadStripeStatus(); }, [load, loadStripeStatus]);

  const setField = useCallback((field, value) => setConfig((prev) => ({ ...prev, [field]: value })), []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        dispatchMode: config.dispatchMode,
        phoneVerificationMode: config.phoneVerificationMode || 'sms',
        blastInitialRadiusMiles: Number(config.blastInitialRadiusMiles),
        taxPercent: Number(config.taxPercent),
        goaFeePercent: Number(config.goaFeePercent),
        towRoadFactor: Number(config.towRoadFactor),
        supportPhone: config.supportPhone || '',
        unlimitedSoftCapPerYear: isBlank(config.unlimitedSoftCapPerYear) ? 12 : Number(config.unlimitedSoftCapPerYear),
        membershipsEnabled: config.membershipsEnabled !== false,
        appDiscount: {
          enabled: !!config.appDiscount?.enabled,
          percentOff: Number(config.appDiscount?.percentOff ?? 10),
          includeWebApp: !!config.appDiscount?.includeWebApp,
          headline: config.appDiscount?.headline || '',
          blurb: config.appDiscount?.blurb || ''
        },
        services: (config.services || []).map((s, i) => ({
          key: s.key,
          label: s.label,
          enabled: s.enabled !== false,
          cashAllowed: s.cashAllowed !== false,
          description: s.description || '',
          icon: s.icon || 'generic',
          sortOrder: i + 1,
          basePrice: Number(s.basePrice),
          includedMiles: Number(s.includedMiles),
          perMileRate: Number(s.perMileRate),
          classPricing: classPricingPayload(s.classPricing) || {},
          classType: s.classType,
          jobService: s.jobService,
          needsDropoff: !!s.needsDropoff
        })),
        regionOverrides: (config.regionOverrides || []).map((o) => ({
          regionId: o.regionId,
          services: (o.services || [])
            .map((s) => {
              const classPricing = classPricingPayload(s.classPricing);
              return {
                key: s.key,
                ...(!isBlank(s.basePrice) ? { basePrice: Number(s.basePrice) } : {}),
                ...(!isBlank(s.includedMiles) ? { includedMiles: Number(s.includedMiles) } : {}),
                ...(!isBlank(s.perMileRate) ? { perMileRate: Number(s.perMileRate) } : {}),
                ...(classPricing ? { classPricing } : {})
              };
            })
            .filter((s) => Object.keys(s).length > 1)
        })),
        plans: plansPayload(config),
        intro: {
          enabled: config.intro?.enabled !== false,
          percentOff: Number(config.intro?.percentOff ?? 0)
        }
      };
      const { data } = await axios.put(`${API}/api/customer-admin/config`, payload, { headers: getAuthHeader() });
      setConfig(data.config);
      notify('Customer app settings saved', 'success');
      loadStripeStatus();
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to save customer app settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  if (!config) {
    // Never spin forever: say what went wrong and offer a retry
    return (
      <Alert severity="error" sx={{ mt: 2 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
        Could not load the customer app settings{loadError ? `: ${loadError}` : ''}. If this says the Owner Console is locked, unlock it and try again.
      </Alert>
    );
  }

  const [title, blurb] = VIEW_TITLE[view] || VIEW_TITLE.services;
  const canSave = view !== 'customers';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 2 }}>
        <Box>
          <Typography variant="h5">{title}</Typography>
          <Typography variant="body2" color="text.secondary">{blurb}</Typography>
        </Box>
        {canSave && (
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        )}
      </Box>

      {view === 'services' && (
        <>
          <DispatchSection config={config} setField={setField} />
          <PricingSection config={config} setField={setField} regions={regions} />
          <AppDiscountSection config={config} setField={setField} />
        </>
      )}
      {view === 'memberships' && (
        <>
          <PlansSection config={config} setField={setField} />
          {/* White Knight's own Stripe keys; a partner in demo mode has no Stripe yet (it connects one under Company setup → Payments) */}
          {isPlatform ? <StripeStatusSection status={stripeStatus} /> : (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" gutterBottom>Payments</Typography>
              <Typography variant="body2" color="text.secondary">
                {demoMode
                  ? 'Demo mode: memberships and service payments are simulated and nothing is charged. Connect your Stripe account and go live under Company setup → Payments.'
                  : 'Live: memberships and service payments are charged on your connected Stripe account. Manage it under Company setup → Payments.'}
              </Typography>
            </Paper>
          )}
        </>
      )}
      {view === 'customers' && (
        <>
          <CustomersSection notify={notify} config={config} />
          <ClaimsSection notify={notify} />
        </>
      )}

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">{snack.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

export default CustomerApp;
